import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageMenu from '../../../blocks/PageMenu/PageMenu';
import IconArrowGray from '../../../../img/icons/svg/botones/flecha-big-gris.svg';
import useDecode from 'hooks/useDecode';
import ManagementFormCardList from 'components/blocks/ManagementNewForms/CardList/ManagementFormCardList';
import Loader from 'components/blocks/Loader/Loader';
import ErrorDisplay from 'components/blocks/ManagementNewForms/ErrorManagement/ErrorPantalla';
import useGetInfoSolicitudCATAFuego from 'services/record/SolicitudCATAFuego/useGetInfoSolicitudCATAFuego';
import useGetSolicitudCATAFuegoByID from 'services/record/SolicitudCATAFuego/useGetSolicitudCATAFuegoByID';
import useGetSolicitudCATAFuegoByUser from 'services/record/SolicitudCATAFuego/useGetSolicitudCATAFuegoByUser';
import SolicitudCATAFuego from './SolicitudCATAFuego';

type HttpStatus = {
    status: number | null;
    statusText: string | null;
};

const GridSolicitudCATAFuego = ({ expedienteId = null, origen = null }) => {
    const {
        getSolicitudCATAFuegoByID,
        loadingGetSolicitudCATAFuegoByID,
        statusGetSolicitudCATAFuegoByID,
    } = useGetSolicitudCATAFuegoByID();

    const {
        getInfoSolicitudCATAFuego,
        loadingGetInfoSolicitudCATAFuego,
        statusGetInfoSolicitudCATAFuego,
    } = useGetInfoSolicitudCATAFuego();

    const {
        dataGetSolicitudCATAFuegoByUser,
        getSolicitudCATAFuegoByUser,
        loadingGetSolicitudCATAFuegoByUser,
        statusGetSolicitudCATAFuegoByUser,
    } = useGetSolicitudCATAFuegoByUser();

    const [currentHttp, setCurrentHttp] = useState<HttpStatus>({
        status: null,
        statusText: null,
    });

    const title = 'Solicitud CATA por fuerza mayor como consecuencia de incendios forestales';
    const navigate = useNavigate();
    const location = useLocation();
    const tokenResponse = useDecode('token');
    const userId = tokenResponse?.extension_id;

    const origenValue = location?.state?.origen ?? origen ?? null;
    const expedienteIdValue = location?.state?.expedienteId ?? expedienteId ?? null;
    const expedienteIdByOrigen = Number(origenValue) === 1 ? (expedienteIdValue ?? null) : null;
    const dataUserWithExpedienteByOrigen = (payload: any) => (
        expedienteIdByOrigen !== null && expedienteIdByOrigen !== undefined
            ? { ...payload, ExpedienteId: expedienteIdByOrigen }
            : payload
    );

    const [dataUser, setDataUser] = useState<any>(null);
    const [info, setInfo] = useState(false);
    const [showGenerateButton, setShowGenerateButton] = useState(true);
    const [formRenderKey, setFormRenderKey] = useState(0);
    const [generateKey, setGenerateKey] = useState(0);
    const activeRequestRef = useRef(0);

    useEffect(() => {
        getSolicitudCATAFuegoByUser(userId, expedienteIdByOrigen);
    }, []);

    useEffect(() => {
        if (statusGetSolicitudCATAFuegoByID?.status) {
            setCurrentHttp({
                status: statusGetSolicitudCATAFuegoByID.status,
                statusText: statusGetSolicitudCATAFuegoByID.statusText,
            });
        } else if (statusGetInfoSolicitudCATAFuego?.status) {
            setCurrentHttp({
                status: statusGetInfoSolicitudCATAFuego.status,
                statusText: statusGetInfoSolicitudCATAFuego.statusText,
            });
        } else if (statusGetSolicitudCATAFuegoByUser?.status) {
            setCurrentHttp({
                status: statusGetSolicitudCATAFuegoByUser.status,
                statusText: statusGetSolicitudCATAFuegoByUser.statusText,
            });
        }
    }, [
        statusGetSolicitudCATAFuegoByID,
        statusGetInfoSolicitudCATAFuego,
        statusGetSolicitudCATAFuegoByUser,
    ]);

    const rowSelected = (args: any) => {
        const requestId = Date.now();
        activeRequestRef.current = requestId;
        setFormRenderKey(prev => prev + 1);
        setDataUser(null);
        setInfo(false);

        getSolicitudCATAFuegoByID(
            args.data.UsuarioId,
            args.data.PrestacionId,
            args.data.Id,
            (payload: any) => {
                if (activeRequestRef.current === requestId) {
                    setDataUser(dataUserWithExpedienteByOrigen(payload));
                }
            }
        );
    };

    const handleInfo = async () => {
        const requestId = Date.now();
        activeRequestRef.current = requestId;
        setFormRenderKey(prev => prev + 1);
        setGenerateKey(prev => prev + 1);
        setDataUser(null);
        setInfo(true);

        await getInfoSolicitudCATAFuego(userId, (payload: any) => {
            if (activeRequestRef.current === requestId) {
                setDataUser(dataUserWithExpedienteByOrigen(payload));
            }
        });
    };

    useEffect(() => {
        const currentForms = dataGetSolicitudCATAFuegoByUser;

        if (Array.isArray(currentForms)) {
            const hasPendingOrAcceptedForm = currentForms.some(item => item.EstadoFormulario === 99 || item.EstadoFormulario === 99);
            setShowGenerateButton(!hasPendingOrAcceptedForm);
        }
    }, [dataGetSolicitudCATAFuegoByUser]);

    const isLoading = () => (
        loadingGetSolicitudCATAFuegoByID ||
        loadingGetInfoSolicitudCATAFuego ||
        (loadingGetSolicitudCATAFuegoByUser && !dataUser)
    );

    return (
        <div className="container bg-#EEEEEE management-new-forms-typography">
            <PageMenu title={title}>
                <div className="btns-header-container">
                    <button aria-label="Volver atrás" onClick={() => navigate(-1)}>
                        <img src={IconArrowGray} alt="" />
                    </button>
                </div>
            </PageMenu>

            <div className="container mb-4">
                <div className="alert alert-info" role="note" aria-label="Información del formulario de incendios">
                    <p className="paragraph mb-0 text-secondary">
                        Con el objetivo de paliar las consecuencias económicas provocadas por los incendios forestales declarados en determinados territorios de las provincias de Toledo y Ávila, así como de la Comunidad de Madrid, el <em>Real Decreto-ley 20/2026</em> regula una prestación extraordinaria de cese de actividad destinada a aquellos trabajadores por cuenta propia cuya actividad profesional haya resultado interrumpida o gravemente afectada como consecuencia directa de los incendios o de las restricciones adoptadas por las autoridades competentes. Esta prestación tiene carácter excepcional y temporal.
                    </p>
                </div>
            </div>

            <div className="container">
                <ManagementFormCardList dataSource={dataGetSolicitudCATAFuegoByUser || []} onSelect={rowSelected} key={generateKey} />
            </div>

            {showGenerateButton && (
                <div className="container">
                    <div className="delta-buttons c-right">
                        <button className="btn-box btn-accent" onClick={handleInfo}>
                            Generar Formulario
                        </button>
                    </div>
                </div>
            )}

            <Loader activeLoader={isLoading()} label="Cargando Formulario" />

            {dataUser && currentHttp.status === 200 && (
                <div className="mt-5">
                    <SolicitudCATAFuego
                        key={`${info ? 'create' : 'edit'}-${dataUser?.Id ?? 'new'}-${formRenderKey}`}
                        dataUser={dataUser}
                        info={info}
                        onSuccess={() => getSolicitudCATAFuegoByUser(userId, expedienteIdByOrigen)}
                    />
                </div>
            )}

            {currentHttp.status && !isLoading() && ![200, 204].includes(currentHttp.status) && (
                <ErrorDisplay descripcionError={currentHttp.statusText} />
            )}
        </div>
    );
};

export default GridSolicitudCATAFuego;